"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon";
import { Button } from "@/components";
import { PRODUCT_PLACEHOLDER_IMAGE } from "@shared/lib/product-placeholder";

/** Wide tile: 557×378; square tile: 378×378, shared visual height (378px) when widths match design. */
const TILE_WIDE =
  "w-[min(557px,92vw)] shrink-0 sm:w-[557px] aspect-[557/378]";
const TILE_SQUARE =
  "w-[min(378px,88vw)] shrink-0 sm:w-[378px] aspect-[378/378]";

const COMMUNITY_TILES = [
  { key: "w1", boxClass: TILE_WIDE },
  { key: "s1", boxClass: TILE_SQUARE },
  { key: "w2", boxClass: TILE_WIDE },
  { key: "s2", boxClass: TILE_SQUARE },
  { key: "w3", boxClass: TILE_WIDE },
  { key: "s3", boxClass: TILE_SQUARE },
] as const;

const SCROLL_STEP_PX = 480;

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";

/**
 * Real photographs across all eight stores.
 *
 * All six tiles rendered the same grey product placeholder, so a section headed
 * "The Dank community" showed no community. Taking one photo from each store in
 * turn, rather than the first six from one store, means the strip shows six
 * different rooms instead of six angles of the same counter.
 */
function useCommunityPhotos(count: number): string[] {
  const [photos, setPhotos] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: "no-store" });
        const data = await res.json();
        const stores: Array<{ photosJson?: string[] | null; imageUrl?: string | null }> =
          data?.stores || [];

        const perStore = stores.map((store) =>
          [...(store.photosJson || []), ...(store.imageUrl ? [store.imageUrl] : [])].filter(Boolean)
        );

        // Round-robin: first photo of every store, then second of every store...
        const picked: string[] = [];
        for (let depth = 0; picked.length < count && depth < 8; depth += 1) {
          for (const list of perStore) {
            if (picked.length >= count) break;
            if (list[depth]) picked.push(list[depth]);
          }
        }
        if (!cancelled) setPhotos(picked);
      } catch {
        // Placeholder art stays; the strip is decorative.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [count]);

  return photos;
}

export default function WhyDankCommunitySection() {
  const photos = useCommunityPhotos(COMMUNITY_TILES.length);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanPrev(scrollLeft > 8);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 8);
  }, []);

  const handleStep = useCallback((direction: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;
    const delta = direction === "next" ? SCROLL_STEP_PX : -SCROLL_STEP_PX;
    el.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScrollState) : null;
    ro?.observe(el);
    window.addEventListener("resize", updateScrollState);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  return (
    <section className="relative w-full" aria-labelledby="why-dank-community-heading">
      <div className="w-full pb-2 pt-2 sm:pb-4 sm:pt-4">
        {/* Header, aligned with `LocationExploreSection` (title + supporting copy on large screens) */}
        <div className="mb-6 grid grid-cols-1 gap-4 lg:mb-8 lg:grid-cols-12 lg:items-start lg:gap-8">
          <h2
            id="why-dank-community-heading"
            className="text-h3-mobile font-bold leading-tight text-text-primary sm:text-h3-desktop lg:col-span-7"
          >
            The Dank community
          </h2>
          <p className="text-body-14-mobile font-medium leading-relaxed text-text-secondary sm:text-body-16-desktop lg:col-span-5 float-right lg:float-none lg:text-right lg:leading-relaxed">
            More than just a store. Check out moments from our dispensaries, 
            happy customers, and the vibrant culture we&apos;re building in YYC
          </p>
        </div>

        <div
          ref={scrollerRef}
          className="-mx-1 flex gap-3 overflow-x-auto scroll-smooth px-1 pb-1 scrollbar-hide sm:gap-4"
          onScroll={updateScrollState}
          role="region"
          aria-label="Community photos"
        >
          {COMMUNITY_TILES.map((tile, index) => {
            const photo = photos[index];
            return (
            <div
              key={tile.key}
              className={`relative overflow-hidden rounded-2xl border border-neutral-10 bg-neutral-10 sm:rounded-2xl ${tile.boxClass}`}
            >
              {/* A real photo fills the tile; the placeholder keeps its inset so
                  the silhouette does not stretch edge to edge. */}
              <div className={photo ? "absolute inset-0" : "absolute inset-[10%] sm:inset-[12%]"}>
                <div className="relative h-full w-full">
                  <Image
                    src={photo || PRODUCT_PLACEHOLDER_IMAGE}
                    alt=""
                    fill
                    className={photo ? "object-cover" : "object-contain object-center scale-90"}
                    sizes="(max-width: 640px) 76vw, 480px"
                    unoptimized={Boolean(photo)}
                  />
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* Nav, explore-style controls; lavender-tinted surfaces per design */}
        <div className="mt-6 flex items-center gap-3 sm:mt-8">
          <div className="h-px flex-1 bg-neutral-10" />
          <Button
            variant="bordered"
            size="icon-sm"
            aria-label="Previous photos"
            disabled={!canPrev}
            onClick={() => handleStep("prev")}
            className="!rounded-full"
          >
            <ChevronLeftIcon size={18} color="currentColor" />
          </Button>
          <Button
            variant="bordered"
            size="icon-sm"
            aria-label="Next photos"
            disabled={!canNext}
            onClick={() => handleStep("next")}
            className="!rounded-full"
          >
            <ChevronRightIcon size={18} color="currentColor" />
          </Button>
          <div className="h-px flex-1 bg-neutral-10" />
        </div>
      </div>
    </section>
  );
}
